/* The floating box. It follows the pointer, or, pinned, stands beside the element it is
   about: a finger has no pointer to follow. It knows nothing about hands: whoever shows it
   hands in the rows, already built as nodes, so nothing in it is ever read as markup. */

import { el, fill } from "./dom";

export interface Tooltip {
  root: HTMLElement;
  /** Put these rows up and place the box by the pointer. */
  show(at: PointerEvent, ...rows: Node[]): void;
  /** Put these rows up beside `anchor`, to stay until `hide()`. The box then takes pointer
      events, so that what it says can be selected and a click on it lands on nothing else. */
  pin(anchor: Element, ...rows: Node[]): void;
  /** Swap the rows of a box that is already up, where it stands. A pinned box is put beside
      `anchor` again, for a caller whose anchor was redrawn. */
  update(anchor: Element | null, ...rows: Node[]): void;
  /** Follow the pointer. Nothing, while pinned. */
  move(at: PointerEvent): void;
  hide(): void;
  readonly shown: boolean;
  readonly pinned: boolean;
}

export function createTooltip(): Tooltip {
  const root = el("div", { class: "tip", role: "tooltip" });
  let anchor: Element | null = null;

  // Kept on screen: a box that would run off the right or bottom edge flips to the other
  // side of the pointer instead.
  const move = (e: PointerEvent): void => {
    if (anchor) return;
    const p = 14;
    const w = root.offsetWidth;
    const h = root.offsetHeight;
    let x = e.clientX + p;
    let y = e.clientY + p;
    if (x + w > innerWidth - 8) x = e.clientX - w - p;
    if (y + h > innerHeight - 8) y = e.clientY - h - p;
    root.style.left = `${x}px`;
    root.style.top = `${y}px`;
  };

  // Below the anchor and to its right, flipped like the hovering box, and never over the
  // anchor itself: on a touch screen that is where the finger is.
  const place = (): void => {
    if (!anchor) return;
    const p = 6;
    const a = anchor.getBoundingClientRect();
    const w = root.offsetWidth;
    const h = root.offsetHeight;
    let x = a.right + p;
    let y = a.bottom + p;
    if (x + w > innerWidth - 8) x = a.left - w - p;
    if (y + h > innerHeight - 8) y = a.top - h - p;
    // A window too small to flip in: as far in as it goes, rather than off the edge.
    root.style.left = `${Math.max(8, Math.min(x, innerWidth - w - 8))}px`;
    root.style.top = `${Math.max(8, y)}px`;
  };
  // The box is fixed to the window and its anchor is not. Capturing: a scroll does not
  // bubble, and the anchor may sit in a scroller of its own.
  addEventListener("scroll", place, { passive: true, capture: true });
  addEventListener("resize", place);

  return {
    root,
    show(at, ...rows) {
      anchor = null;
      root.classList.remove("pinned");
      fill(root, rows);
      root.classList.add("show");
      move(at);
    },
    pin(to, ...rows) {
      anchor = to;
      fill(root, rows);
      root.classList.add("show", "pinned");
      place();
    },
    update(to, ...rows) {
      fill(root, rows);
      if (!anchor) return;
      if (to) anchor = to;
      place();
    },
    move,
    hide() {
      anchor = null;
      root.classList.remove("show", "pinned");
    },
    get shown() {
      return root.classList.contains("show");
    },
    get pinned() {
      return anchor !== null;
    },
  };
}
